import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Droplet, CalendarIcon, Truck, CreditCard, Banknote, Zap, Package, FileText, Download } from "lucide-react";

const STATUS_COLORS = {
  "Order Placed": "bg-amber-100 text-amber-800",
  "Pickup Scheduled": "bg-stone-100 text-stone-800",
  "Picked Up": "bg-blue-100 text-blue-800",
  "In Process": "bg-pink-100 text-pink-800",
  "Out for Delivery": "bg-purple-100 text-purple-800",
  "Delivered": "bg-emerald-100 text-emerald-800",
  "Cancelled": "bg-red-100 text-red-800",
};

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(true);
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${id}`);
        setB(data);
      } catch (err) {
        toast.error("Booking not found");
        navigate("/dashboard");
      } finally {
        setBusy(false);
      }
    })();
  }, [id, user, navigate]);

  const payNow = async () => {
    setPayLoading(true);
    try {
      if (b.payment_method === "paypal") {
        const { data: order } = await api.post("/payments/paypal/create-order", {
          booking_id: b.booking_id,
          origin_url: window.location.origin,
        });
        window.location.href = order.url;
      } else {
        const { data: sess } = await api.post("/payments/checkout/session", {
          booking_id: b.booking_id,
          origin_url: window.location.origin,
        });
        window.location.href = sess.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
      setPayLoading(false);
    }
  };

  if (busy || !b) return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">Loading…</div>
    </div>
  );

  return (
    <div data-testid="booking-detail-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-stone-500 hover:text-pink-600 text-sm font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500">Booking #{b.booking_id.slice(-6).toUpperCase()}</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter text-stone-900 mt-1">{b.service_tier}</h1>
          </div>
          <Badge className={`${STATUS_COLORS[b.status]} rounded-full font-bold text-sm px-4 py-1.5`} data-testid="booking-status-badge">
            {b.status}
          </Badge>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <InfoTile icon={<CalendarIcon className="w-5 h-5" />} label="Pickup" value={`${b.pickup_date}`} sub={b.pickup_time_window} />
          <InfoTile icon={b.express ? <Zap className="w-5 h-5" /> : <Truck className="w-5 h-5" />} label="Delivery" value={b.express ? "Express" : "Standard"} sub={b.express ? "Same day" : "24 hours"} />
          <InfoTile icon={<Droplet className="w-5 h-5" />} label="Detergent" value={b.detergent} />
        </div>

        <Card className="p-6 rounded-2xl border border-stone-200 bg-white mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Pickup details</div>
          <div className="space-y-3 text-stone-800">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-pink-500 mt-1" /><span>{b.address}</span></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-pink-500" /><span>{b.phone}</span></div>
            {b.notes && <div className="text-sm text-stone-600 pl-6 italic">&ldquo;{b.notes}&rdquo;</div>}
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border border-stone-200 bg-white mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Payment</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-stone-800">
              {b.payment_method === "stripe" ? <CreditCard className="w-4 h-4 text-pink-500" /> :
                b.payment_method === "paypal" ? <img alt="PayPal" src="https://cdn.simpleicons.org/paypal/003087" className="w-4 h-4" /> :
                b.payment_method === "card_at_door" ? <CreditCard className="w-4 h-4 text-pink-500" /> :
                <Banknote className="w-4 h-4 text-pink-500" />}
              <span className="font-semibold capitalize">{
                b.payment_method === "stripe" ? "Card online (Stripe)" :
                b.payment_method === "paypal" ? "PayPal" :
                b.payment_method === "card_at_door" ? "Card at door" :
                "Cash at door"
              }</span>
              <Badge className="ml-2 rounded-full">{b.payment_status}</Badge>
            </div>
            <div className="font-heading font-black text-3xl text-stone-900">${b.price}</div>
          </div>
          {(b.payment_method === "stripe" || b.payment_method === "paypal") && b.payment_status !== "paid" && (
            <Button
              data-testid="pay-now-btn"
              onClick={payNow}
              disabled={payLoading}
              className="mt-5 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-11 px-6"
            >
              {payLoading ? "Redirecting…" : `Pay now with ${b.payment_method === "paypal" ? "PayPal" : "card"}`}
            </Button>
          )}
        </Card>

        <Card className="p-6 rounded-2xl border border-stone-200 bg-white mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400">Invoice</div>
              <div className="font-heading font-bold text-stone-900 mt-1">{b.invoice_number || `#${b.booking_id.slice(-6).toUpperCase()}`}</div>
              <div className="text-xs text-stone-500">Subtotal ${(b.subtotal || 0).toFixed(2)} · Tax ${(b.tax_total || 0).toFixed(2)} · Total ${(b.price || 0).toFixed(2)}</div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="invoice-view-btn"
                variant="outline"
                onClick={() => {
                  const token = localStorage.getItem("wp_token");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${b.booking_id}/html`;
                  // Open in new tab with token via fetch → blob (backend requires auth)
                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => r.blob())
                    .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
                }}
                className="rounded-full font-bold border-stone-300"
              >
                <FileText className="w-4 h-4 mr-1" /> View
              </Button>
              <Button
                data-testid="invoice-download-btn"
                onClick={async () => {
                  const token = localStorage.getItem("wp_token");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${b.booking_id}/pdf`;
                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                  const blob = await r.blob();
                  const dlUrl = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = dlUrl;
                  a.download = `${b.invoice_number || b.booking_id}.pdf`;
                  a.click();
                  URL.revokeObjectURL(dlUrl);
                }}
                className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold"
              >
                <Download className="w-4 h-4 mr-1" /> PDF
              </Button>
            </div>
          </div>
        </Card>

        <StatusTimeline status={b.status} />
      </div>
    </div>
  );
}

function InfoTile({ icon, label, value, sub }) {
  return (
    <Card className="p-5 rounded-2xl border border-stone-200 bg-white">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center">{icon}</div>
        <div>
          <div className="text-xs uppercase font-bold tracking-widest text-stone-400">{label}</div>
          <div className="font-heading font-bold text-stone-900">{value}</div>
          {sub && <div className="text-xs text-stone-500">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

function StatusTimeline({ status }) {
  const steps = ["Order Placed", "Pickup Scheduled", "Picked Up", "In Process", "Out for Delivery", "Delivered"];
  const idx = steps.indexOf(status);
  return (
    <Card className="p-6 rounded-2xl border border-stone-200 bg-white">
      <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4">Order progress</div>
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-2 flex-1">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-black text-sm ${
                i <= idx ? "bg-pink-500 text-white" : "bg-stone-200 text-stone-500"
              }`}>{i + 1}</div>
              <div className={`text-xs text-center font-semibold ${i <= idx ? "text-stone-900" : "text-stone-400"}`}>{s}</div>
            </div>
            {i < steps.length - 1 && <div className={`flex-1 h-0.5 ${i < idx ? "bg-pink-500" : "bg-stone-200"}`} />}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
}
